import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Card, CardContent, Chip, Alert, CircularProgress, FormControl, InputLabel, Select, MenuItem, OutlinedInput, ListItemText, Checkbox,
  ButtonGroup, Divider
} from "@mui/material";
import { Add, Delete, Edit, PlayArrow, Refresh, Category } from "@mui/icons-material";
import axios from "axios";

const API_BASE = "/api/tools";

export default function ApiToolsPage() {
  const [apiTools, setApiTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openTemplateForm, setOpenTemplateForm] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [testDialog, setTestDialog] = useState({ open: false, result: "", title: "" });
  
  // 폼 상태
  const [form, setForm] = useState({
    api_name: "",
    base_url: "",
    api_key: "",
    headers: "",
    description: "",
    target_knowledge_bases: [],
    auth_type: "api_key",
    client_id: "",
    client_secret: "",
    mall_id: "",
    refresh_token: ""
  });
  const [formError, setFormError] = useState("");

  // 템플릿 폼 상태 추가
  const [templateForm, setTemplateForm] = useState({
    template_id: "cafe24_bundle",
    mall_id: "",
    client_id: "",
    client_secret: "",
    refresh_token: "",
    target_knowledge_bases: [],
    description_prefix: ""
  });
  const [templateError, setTemplateError] = useState("");
  const [templates, setTemplates] = useState([]);

  const [configurations, setConfigurations] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [testResults, setTestResults] = useState({});

  // 템플릿 목록 조회
  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/tools/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('템플릿 목록 조회 오류:', error);
    }
  };

  // 목록 조회
  const fetchApiTools = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/configurations`);
      setApiTools(res.data);
    } catch (e) {
      setSnackbar({ 
        open: true, 
        message: "API 도구 목록을 불러오지 못했습니다.", 
        severity: "error" 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchKnowledgeBases = async () => {
    try {
      const response = await fetch('/api/tools/knowledge-bases');
      if (response.ok) {
        const data = await response.json();
        setKnowledgeBases(data);
      } else {
        console.error('지식베이스 목록 조회 실패');
      }
    } catch (error) {
      console.error('지식베이스 목록 조회 오류:', error);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const response = await fetch('/api/tools/configurations');
      if (response.ok) {
        const data = await response.json();
        setConfigurations(data.configurations || {});
      } else {
        console.error('API 설정 조회 실패');
      }
    } catch (error) {
      console.error('API 설정 조회 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiTools();
    fetchKnowledgeBases();
    fetchConfigurations();
    fetchTemplates();
  }, []);

  // 삭제
  const handleDelete = async (api_name) => {
    if (!window.confirm(`API 도구 '${api_name}'을(를) 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API_BASE}/configurations/${api_name}`);
      setSnackbar({ open: true, message: "삭제 완료", severity: "success" });
      fetchApiTools();
    } catch {
      setSnackbar({ open: true, message: "삭제 실패", severity: "error" });
    }
  };

  // 테스트
  const handleTest = async (api_name) => {
    let test_params = {};
    if (api_name === "stock_api" || api_name === "price_api") test_params.product_id = "TEST-PRODUCT";
    if (api_name === "stock_api") test_params.size = "280mm";
    if (api_name === "order_api") test_params.order_id = "TEST-ORDER";
    
    try {
      setTestDialog({ open: true, result: "테스트 중...", title: `${api_name} 테스트` });
      const res = await axios.post(`${API_BASE}/test`, { api_name, test_params });
      setTestDialog({ 
        open: true, 
        result: res.data.test_result || res.data.message, 
        title: `${api_name} 테스트 결과` 
      });
    } catch (e) {
      setTestDialog({ 
        open: true, 
        result: e?.response?.data?.detail || "테스트 실패", 
        title: `${api_name} 테스트 실패` 
      });
    }
  };

  // 추가/수정 폼 열기
  const handleOpenForm = (tool = null) => {
    if (tool) {
      const oauthConfig = tool.oauth_config || {};
      setForm({
        api_name: tool.api_name,
        base_url: tool.base_url,
        api_key: "",
        headers: JSON.stringify(tool.headers || {}, null, 2),
        description: tool.description || "",
        target_knowledge_bases: tool.target_knowledge_bases || [],
        auth_type: oauthConfig.auth_type || "api_key",
        client_id: oauthConfig.client_id || "",
        client_secret: "",
        mall_id: oauthConfig.mall_id || "",
        refresh_token: ""
      });
      setEditTool(tool);
    } else {
      setForm({
        api_name: "",
        base_url: "",
        api_key: "",
        headers: "{\n  \"Content-Type\": \"application/json\"\n}",
        description: "",
        target_knowledge_bases: [],
        auth_type: "api_key",
        client_id: "",
        client_secret: "",
        mall_id: "",
        refresh_token: ""
      });
      setEditTool(null);
    }
    setFormError("");
    setOpenForm(true);
  };

  // 폼 닫기
  const handleCloseForm = () => {
    setOpenForm(false);
    setEditTool(null);
    setFormError("");
  };

  // 폼 입력 처리
  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // 폼 제출
  const handleSubmit = async () => {
    if (!form.api_name || !form.base_url) {
      setFormError("API 이름과 Base URL은 필수입니다.");
      return;
    }
    
    if (form.auth_type === "oauth2" && (!form.client_id || !form.client_secret || !form.mall_id)) {
      setFormError("OAuth 2.0 모드에서는 Client ID, Client Secret, Mall ID가 필수입니다.");
      return;
    }
    
    let headersObj = {};
    try {
      headersObj = JSON.parse(form.headers || "{}");
    } catch {
      setFormError("Headers는 유효한 JSON 형식이어야 합니다.");
      return;
    }
    try {
      await axios.post(`${API_BASE}/configure`, {
        api_name: form.api_name,
        base_url: form.base_url,
        api_key: form.api_key || undefined,
        headers: headersObj,
        description: form.description,
        target_knowledge_bases: form.target_knowledge_bases,
        auth_type: form.auth_type,
        client_id: form.client_id || undefined,
        client_secret: form.client_secret || undefined,
        mall_id: form.mall_id || undefined,
        refresh_token: form.refresh_token || undefined
      });
      setSnackbar({ 
        open: true, 
        message: editTool ? "수정 완료" : "추가 완료", 
        severity: "success" 
      });
      handleCloseForm();
      fetchApiTools();
    } catch (e) {
      setFormError(e?.response?.data?.detail || "저장 실패");
    }
  };

  const getApiTypeChip = (apiName) => {
    if (apiName.includes('stock')) {
      return <Chip label="재고 API" color="primary" size="small" />;
    } else if (apiName.includes('price')) {
      return <Chip label="가격 API" color="secondary" size="small" />;
    } else if (apiName.includes('order')) {
      return <Chip label="주문 API" color="success" size="small" />;
    }
    return <Chip label="기타 API" color="default" size="small" />;
  };

  const getKnowledgeBaseNames = (kbIds) => {
    if (!kbIds || kbIds.length === 0) {
      return <Chip label="모든 지식베이스" color="default" size="small" />;
    }
    
    return kbIds.map(kbId => {
      const kb = knowledgeBases.find(kb => kb.kb_id === kbId);
      const name = kb ? kb.name : kbId;
      return <Chip key={kbId} label={name} color="info" size="small" style={{ margin: '2px' }} />;
    });
  };

  const handleKnowledgeBaseChange = (event) => {
    const value = event.target.value;
    setForm({
      ...form,
      target_knowledge_bases: typeof value === 'string' ? value.split(',') : value
    });
  };

  // 템플릿 폼 열기
  const handleOpenTemplateForm = () => {
    setTemplateForm({
      template_id: "cafe24_bundle",
      mall_id: "",
      client_id: "",
      client_secret: "",
      refresh_token: "",
      target_knowledge_bases: [],
      description_prefix: ""
    });
    setTemplateError("");
    setOpenTemplateForm(true);
  };

  // 템플릿 폼 닫기
  const handleCloseTemplateForm = () => {
    setOpenTemplateForm(false);
    setTemplateError("");
  };

  // 템플릿 폼 입력 처리
  const handleTemplateFormChange = (e) => {
    setTemplateForm({ ...templateForm, [e.target.name]: e.target.value });
  };

  // 템플릿 지식베이스 변경
  const handleTemplateKnowledgeBaseChange = (event) => {
    const value = event.target.value;
    setTemplateForm({
      ...templateForm,
      target_knowledge_bases: typeof value === 'string' ? value.split(',') : value
    });
  };

  // 템플릿 제출
  const handleTemplateSubmit = async () => {
    if (!templateForm.mall_id || !templateForm.client_id || !templateForm.client_secret || !templateForm.refresh_token) {
      setTemplateError("모든 필수 필드를 입력해주세요.");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/create-bundle`, templateForm);
      setSnackbar({ 
        open: true, 
        message: response.data.message || "API 번들이 성공적으로 생성되었습니다.", 
        severity: "success" 
      });
      handleCloseTemplateForm();
      fetchApiTools();
    } catch (e) {
      setTemplateError(e?.response?.data?.detail || "API 번들 생성에 실패했습니다.");
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, p: 2 }}>
      {/* 헤더 */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          API 도구 관리
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />} 
            onClick={fetchApiTools} 
            sx={{ mr: 2 }}
            disabled={loading}
          >
            새로고침
          </Button>
          <ButtonGroup variant="contained" sx={{ mr: 1 }}>
            <Button 
              startIcon={<Add />} 
              onClick={() => handleOpenForm()}
            >
              개별 추가
            </Button>
            <Button 
              startIcon={<Category />} 
              onClick={handleOpenTemplateForm}
              color="secondary"
            >
              템플릿으로 추가
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* 안내 메시지 */}
      <Alert severity="info" sx={{ mb: 3 }}>
        실시간 API 호출 기능을 위한 외부 API 설정을 관리합니다. 
        사용자가 재고, 가격, 주문상태 관련 질문을 하면 LLM이 자동으로 해당 API를 호출합니다.
      </Alert>

      {/* API 도구 목록 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>API 이름</TableCell>
              <TableCell>타입</TableCell>
              <TableCell>Base URL</TableCell>
              <TableCell>설명</TableCell>
              <TableCell>연결된 지식베이스</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="center">동작</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiTools.map((tool) => (
              <TableRow key={tool.api_name}>
                <TableCell>
                  <Chip 
                    label={tool.api_name} 
                    color={
                      tool.api_name === "stock_api" ? "primary" :
                      tool.api_name === "price_api" ? "secondary" :
                      tool.api_name === "order_api" ? "success" : "default"
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{getApiTypeChip(tool.api_name)}</TableCell>
                <TableCell>{tool.base_url}</TableCell>
                <TableCell>{tool.description}</TableCell>
                <TableCell>
                  {getKnowledgeBaseNames(tool.target_knowledge_bases)}
                </TableCell>
                <TableCell>
                  {testResults[tool.api_name]?.loading ? (
                    <CircularProgress size={20} />
                  ) : testResults[tool.api_name]?.success ? (
                    <Chip label="연결됨" color="success" size="small" />
                  ) : testResults[tool.api_name]?.success === false ? (
                    <Chip label="연결 실패" color="error" size="small" />
                  ) : (
                    <Chip label="미테스트" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    onClick={() => handleTest(tool.api_name)} 
                    title="연결 테스트"
                    color="primary"
                  >
                    <PlayArrow />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleOpenForm(tool)} 
                    title="수정"
                    color="secondary"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(tool.api_name)} 
                    title="삭제"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {apiTools.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    등록된 API 도구가 없습니다. 새 API 도구를 추가해보세요.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography>로딩 중...</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 추가/수정 폼 */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {editTool ? `API 도구 수정: ${editTool.api_name}` : "새 API 도구 추가"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="API 이름"
              name="api_name"
              value={form.api_name}
              onChange={handleFormChange}
              disabled={!!editTool}
              required
              helperText={editTool ? "API 이름은 수정할 수 없습니다" : "예: stock_api, price_api, order_api"}
            />
            <TextField
              label="Base URL"
              name="base_url"
              value={form.base_url}
              onChange={handleFormChange}
              required
              helperText="예: https://api.example.com"
            />
            <TextField
              label="API 키"
              name="api_key"
              value={form.api_key}
              onChange={handleFormChange}
              type="password"
              autoComplete="new-password"
              helperText={editTool ? "새 API 키를 입력하면 기존 키가 대체됩니다" : "선택사항"}
            />
            <TextField
              label="Headers (JSON)"
              name="headers"
              value={form.headers}
              onChange={handleFormChange}
              multiline
              minRows={3}
              maxRows={8}
              helperText="API 요청 시 사용할 헤더를 JSON 형식으로 입력"
            />
            <TextField
              label="설명"
              name="description"
              value={form.description}
              onChange={handleFormChange}
              multiline
              minRows={2}
              helperText="이 API 도구의 용도나 설명"
            />
            
            {/* 인증 방식 선택 */}
            <FormControl fullWidth>
              <InputLabel>인증 방식</InputLabel>
              <Select
                name="auth_type"
                value={form.auth_type}
                onChange={handleFormChange}
                label="인증 방식"
              >
                <MenuItem value="api_key">API 키</MenuItem>
                <MenuItem value="oauth2">OAuth 2.0 (Cafe24)</MenuItem>
              </Select>
            </FormControl>
            
            {/* OAuth 2.0 설정 (auth_type이 oauth2일 때만 표시) */}
            {form.auth_type === "oauth2" && (
              <>
                <TextField
                  label="Mall ID"
                  name="mall_id"
                  value={form.mall_id}
                  onChange={handleFormChange}
                  required
                  helperText="Cafe24 쇼핑몰 ID (예: yourmall)"
                />
                <TextField
                  label="Client ID"
                  name="client_id"
                  value={form.client_id}
                  onChange={handleFormChange}
                  required
                  helperText="Cafe24 앱 개발자 센터에서 발급받은 Client ID"
                />
                <TextField
                  label="Client Secret"
                  name="client_secret"
                  value={form.client_secret}
                  onChange={handleFormChange}
                  type="password"
                  required
                  helperText="Cafe24 앱 개발자 센터에서 발급받은 Client Secret"
                />
                <TextField
                  label="Refresh Token"
                  name="refresh_token"
                  value={form.refresh_token}
                  onChange={handleFormChange}
                  type="password"
                  helperText="OAuth 인증 후 발급받은 Refresh Token (액세스 토큰 갱신용)"
                />
              </>
            )}
            
            <FormControl fullWidth>
              <InputLabel>연결할 지식베이스</InputLabel>
              <Select
                multiple
                value={form.target_knowledge_bases}
                onChange={handleKnowledgeBaseChange}
                input={<OutlinedInput label="연결할 지식베이스" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return '모든 지식베이스에서 사용 가능';
                  }
                  return selected.map(kbId => {
                    const kb = knowledgeBases.find(kb => kb.kb_id === kbId);
                    return kb ? kb.name : kbId;
                  }).join(', ');
                }}
              >
                {knowledgeBases.map((kb) => (
                  <MenuItem key={kb.kb_id} value={kb.kb_id}>
                    <Checkbox checked={form.target_knowledge_bases.indexOf(kb.kb_id) > -1} />
                    <ListItemText 
                      primary={kb.name} 
                      secondary={kb.description || kb.kb_id}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formError && (
              <Alert severity="error">{formError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editTool ? "수정" : "추가"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 테스트 결과 다이얼로그 */}
      <Dialog 
        open={testDialog.open} 
        onClose={() => setTestDialog({ open: false, result: "", title: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{testDialog.title}</DialogTitle>
        <DialogContent>
          <Card variant="outlined">
            <CardContent>
              <Typography component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                {testDialog.result}
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog({ open: false, result: "", title: "" })}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cafe24 API 번들 생성 다이얼로그 */}
      <Dialog open={openTemplateForm} onClose={handleCloseTemplateForm} maxWidth="md" fullWidth>
        <DialogTitle>
          🛍️ Cafe24 API 번들 생성
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Cafe24 쇼핑몰의 재고/가격/주문 API를 한번에 설정합니다. 
            기본 정보만 입력하면 3개의 API가 자동으로 생성됩니다.
          </Alert>
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Mall ID"
              name="mall_id"
              value={templateForm.mall_id}
              onChange={handleTemplateFormChange}
              required
              helperText="Cafe24 쇼핑몰 ID (예: yourmall)"
              placeholder="yourmall"
            />
            <TextField
              label="Client ID"
              name="client_id"
              value={templateForm.client_id}
              onChange={handleTemplateFormChange}
              required
              helperText="Cafe24 앱 개발자 센터에서 발급받은 Client ID"
            />
            <TextField
              label="Client Secret"
              name="client_secret"
              value={templateForm.client_secret}
              onChange={handleTemplateFormChange}
              type="password"
              required
              helperText="Cafe24 앱 개발자 센터에서 발급받은 Client Secret"
            />
            <TextField
              label="Refresh Token"
              name="refresh_token"
              value={templateForm.refresh_token}
              onChange={handleTemplateFormChange}
              type="password"
              required
              helperText="OAuth 인증 후 발급받은 Refresh Token"
            />
            <TextField
              label="설명 접두사 (선택사항)"
              name="description_prefix"
              value={templateForm.description_prefix}
              onChange={handleTemplateFormChange}
              helperText="API 설명에 추가할 접두사 (예: '피파스포츠 - ')"
              placeholder="피파스포츠 - "
            />
            
            <FormControl fullWidth>
              <InputLabel>연결할 지식베이스</InputLabel>
              <Select
                multiple
                value={templateForm.target_knowledge_bases}
                onChange={handleTemplateKnowledgeBaseChange}
                input={<OutlinedInput label="연결할 지식베이스" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return '모든 지식베이스에서 사용 가능';
                  }
                  return selected.map(kbId => {
                    const kb = knowledgeBases.find(kb => kb.kb_id === kbId);
                    return kb ? kb.name : kbId;
                  }).join(', ');
                }}
              >
                {knowledgeBases.map((kb) => (
                  <MenuItem key={kb.kb_id} value={kb.kb_id}>
                    <Checkbox checked={templateForm.target_knowledge_bases.indexOf(kb.kb_id) > -1} />
                    <ListItemText 
                      primary={kb.name} 
                      secondary={kb.description || kb.kb_id}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Divider />
            
            <Typography variant="h6" color="primary">
              생성될 API 목록:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • 📦 <strong>재고 확인 API</strong> - 상품 재고량과 구매 가능 여부 확인
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • 💰 <strong>가격 조회 API</strong> - 상품 가격과 할인 정보 확인  
              </Typography>
              <Typography variant="body2">
                • 📋 <strong>주문 상태 API</strong> - 주문 진행 상황과 배송 정보 확인
              </Typography>
            </Box>
            
            {templateError && (
              <Alert severity="error">{templateError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateForm}>취소</Button>
          <Button variant="contained" onClick={handleTemplateSubmit} startIcon={<Add />}>
            API 번들 생성
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "", severity: "info" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setSnackbar({ open: false, message: "", severity: "info" })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 